import { 
    AxiosInstance, 
    AxiosError, 
    InternalAxiosRequestConfig 
  } from 'axios';

import Keycloak from 'keycloak-js';

interface KeycloakTokenParsed {
    exp: number;             // Expiration time
    iat: number;             // Issued at time
    auth_time: number;       // Authentication time
    jti: string;             // JWT ID
    iss: string;             // Issuer
    sub: string;             // Subject (user ID)
    typ: string;             // Token type
    azp: string;             // Authorized party
    realm_access: {
      roles: string[];
    };
    resource_access: {
      [key: string]: {
        roles: string[];
      };
    };
}

interface KeycloakTokens {
    token: string;              // Access token
    refreshToken: string;
    idToken?: string;          // OpenID Connect ID token
    tokenParsed: KeycloakTokenParsed;
  }
  
interface KeycloakConfig {
    keycloak: Keycloak;
    axiosInstance: AxiosInstance;
    onAuthError?: (error: Error) => void;
    tokenMinValidity?: number;  // Minimum token validity time in seconds
    enableLogger?: boolean;
  }
  
class KeycloakAuthError extends Error {
    constructor(message: string = 'Keycloak authentication failed') {
      super(message);
      this.name = 'KeycloakAuthError';
    }
  }
  
class KeycloakAxiosInterceptor {
    private readonly keycloak: Keycloak;
    private readonly axiosInstance: AxiosInstance;
    private readonly config: KeycloakConfig;
    private refreshPromise: Promise<boolean> | null = null;
    private readonly tokenMinValidity: number;
    private readonly logger: (message: string, ...args: any[]) => void;
  
    constructor(config: KeycloakConfig) {
      this.keycloak = config.keycloak;
      this.axiosInstance = config.axiosInstance;
      this.config = config;
      this.tokenMinValidity = config.tokenMinValidity || 30; // Default 30 seconds
      this.logger = config.enableLogger 
        ? console.log.bind(console, '[KeycloakAxios]')
        : () => {};
  
      this.setupInterceptors();
    }
  
    private setupInterceptors(): void {
      // Request interceptor
      this.axiosInstance.interceptors.request.use(
        async (config) => {
          try {
            if (this.keycloak.isTokenExpired(this.tokenMinValidity)) {
              this.logger('Token expired, attempting refresh');
              console.log("Token expired, attempting refresh");
              await this.refreshToken();
            }
            config.headers.Authorization = `Bearer ${this.keycloak.token}`;
            return config;
          } catch (error) {
            this.logger('Request interceptor error:', error);
            console.log("Request interceptor error:", error);
            throw error;
          }
        },
        (error) => Promise.reject(error)
      );
  
      // Response interceptor
      this.axiosInstance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
          const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
  
          if (this.shouldAttemptRefresh(error, originalRequest)) {
            try {
              this.logger('Attempting token refresh after 401');
              console.log("Attempting token refresh after 401");
              await this.refreshToken();
              return this.retryOriginalRequest(originalRequest);
            } catch (refreshError) {
              this.logger('Refresh failed after 401:', refreshError);
              console.log('Refresh failed after 401:', refreshError);
              this.handleRefreshError(refreshError as Error);
              throw refreshError;
            }
          }

          if (error.response) {
            // Get the server's error response data
            const serverError = error.response.data as any;

            console.log("Returning a server error:", serverError);
            return Promise.reject({
                status: error.response.status,
                message: serverError.message || 'An error occurred',
                error: serverError.error || error.response.statusText,
                // Include other server response fields if needed
                timestamp: serverError.timestamp
            });
        } else if (error.request) {
            return Promise.reject({
                status: 503,
                message: 'Service is unavailable',
                error: 'Network Error'
            });
        } else {
            return Promise.reject({
                status: 500,
                message: error.message,
                error: 'Unknown Error'
            });
          }
        }
      );
    }
  
    private async updateTokenIfNeeded(): Promise<void> {
      if (this.keycloak.isTokenExpired(this.tokenMinValidity)) {
        this.logger('Token needs update');
        await this.refreshToken();
      }
    }
  
    private shouldAttemptRefresh(
      error: AxiosError, 
      request?: InternalAxiosRequestConfig & { _retry?: boolean }
    ): boolean {
      return (
        error.response?.status === 401 && 
        request !== undefined && 
        !request._retry &&
        this.keycloak.authenticated === true
      );
    }
  
    private async refreshToken(): Promise<boolean> {
      try {
        // Use existing refresh promise if one is in progress
        if (this.refreshPromise) {
          this.logger('Using existing refresh promise');
          return this.refreshPromise;
        }
  
        this.logger('Starting new token refresh');
        this.refreshPromise = (async () => {
          try {
            const refreshed = await this.keycloak.updateToken(this.tokenMinValidity);
            if (refreshed) {
              this.logger('Token refreshed successfully');
              console.log("Token refreshed successfully");
              this.updateAuthHeader();
            } else {
              this.logger('Token is still valid, no refresh needed');
              console.log("Token is still valid, no refresh needed");
            }
            return refreshed;
          } catch (error) {
            this.logger('Token refresh failed:', error);
            console.log('Token refresh failed:', error);
            throw new KeycloakAuthError('Failed to refresh token');
          }
        })();
  
        return await this.refreshPromise;
      } finally {
        this.refreshPromise = null;
      }
    }
  
    private async retryOriginalRequest(
      originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }
    ): Promise<any> {
      try {
        originalRequest._retry = true;
        // Ensure we have the latest token
        originalRequest.headers.Authorization = `Bearer ${this.keycloak.token}`;
        console.log("Retrying original request");
        return await this.axiosInstance(originalRequest);
      } catch (error) {
        this.logger('Retry request failed:', error);
        console.log("Retry request failed:", error);
        throw error;
      }
    }
  
    private handleRefreshError(error: Error): void {
      this.logger('Handling refresh error:', error);
      console.log("Handling refresh error:", error);
      if (this.config.onAuthError) {
        this.config.onAuthError(error);
      }
  
      // Trigger Keycloak login if refresh fails
      //TODO RENABLE this.keycloak.login();
    }
  
    private updateAuthHeader(): void {
      this.axiosInstance.defaults.headers.common['Authorization'] = 
        `Bearer ${this.keycloak.token}`;
    }
  
    // Public methods for manual token management
    public async validateToken(): Promise<boolean> {
      try {
        return !this.keycloak.isTokenExpired(this.tokenMinValidity);
      } catch (error) {
        this.logger('Token validation error:', error);
        return false;
      }
    }
  
    public getTokenInfo(): Partial<KeycloakTokens> | null {
      if (!this.keycloak.authenticated) {
        return null;
      }
  
      return {
        token: this.keycloak.token,
        refreshToken: this.keycloak.refreshToken,
        idToken: this.keycloak.idToken,
        tokenParsed: this.keycloak.tokenParsed as KeycloakTokenParsed
      };
    }
  
    public getUserRoles(): string[] {
      return this.keycloak.tokenParsed?.realm_access?.roles || [];
    }
  
    public hasRole(role: string): boolean {
      return this.keycloak.hasRealmRole(role);
    }
  }
  
  // Factory function to create and set up the interceptor
  export function setupKeycloakInterceptor(
    config: KeycloakConfig
  ): KeycloakAxiosInterceptor {
    return new KeycloakAxiosInterceptor(config);
  }
  
  // Export types and classes
  export type { KeycloakConfig, KeycloakTokens };
  export { KeycloakAuthError };
  
